/**
 * Handing bytes to the browser as a file.
 *
 * One implementation, shared by the report PDF and the CSV exports, because the
 * one non-obvious part of it was paid for once already: the object URL must be
 * revoked on a **timeout** and not synchronously — Safari reads the blob after the
 * click handler has returned, so revoking immediately saves an empty file. A second
 * copy of this in a CSV path would be a second place to get that wrong.
 */

/** Save `blob` under `filename`, without navigating away from the page. */
export function saveBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob)
  try {
    const link = document.createElement('a')
    link.href = url
    link.download = filename
    document.body.appendChild(link)
    link.click()
    link.remove()
  } finally {
    window.setTimeout(() => URL.revokeObjectURL(url), 10_000)
  }
}

/**
 * The UTF-8 byte-order mark, as an escape and never as the literal character — a
 * BOM pasted into source is invisible and the next reader deletes it as a stray.
 *
 * Not decoration: Excel on Windows reads a BOM-less file as the local code page,
 * and these exports carry em dashes and curly quotes straight out of a model's
 * prose, so without it every rationale opens as mojibake — which reads as corrupt
 * data rather than as a missing byte.
 */
const BOM = '\uFEFF'

/**
 * A CSV string as a download.
 *
 * The BOM is prepended here and not in `toCsv`, so the serialiser stays byte-exact
 * for any consumer that is not a spreadsheet.
 */
export function downloadCsv(filename: string, csv: string): void {
  saveBlob(new Blob([BOM, csv], { type: 'text/csv;charset=utf-8' }), filename)
}
